import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import InputBase from "@mui/material/InputBase";
import Typography from "@mui/material/Typography";
import { useId, useState } from "react";

import MarketingLottie from "./MarketingLottie";
import { ACCENT, TINY, marketingPalette } from "./tokens";

/**
 * The published site's form parts: `.pop-up_lbl-block` fields, the
 * disclaimer beside `.hack-button`, and the `.w-form-done` and `.w-form-fail`
 * states. Webflow set every form of the site from the same classes, so the
 * sales inquiry dialog and the enterprise trial page draw from here.
 */

const SUBMIT_LABEL = "Send";
/** The published button's own `data-wait`. */
const SUBMIT_WAIT_LABEL = "Please wait...";

/** Enough to catch a typo. The inbox is the real check. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_INVALID = "Please enter a valid email address";

const DISCLAIMER =
  "Webshare.io will process your data to manage your inquiry and inform you about our services. For more information, visit our ";

/** The published `.w-form-fail` text, verbatim. */
const FAILED_TEXT = "Oops! Something went wrong while submitting the form.";
/** The published success animation, mirrored from Webflow's CDN, at its 224px. */
const SENT_LOTTIE = "/marketing/nav/success.json";
const SENT_LOTTIE_SIZE = 224;

/* Published fills with no palette role of their own. */
const FIELD_RULE = "rgba(79, 92, 101, 0.4)";
/** Webflow's own input default, which the published fields never override. */
const FIELD_INK = "#333333";
const FIELD_SHADOW = "rgba(139, 153, 190, 0.25)";
const FIELD_SHADOW_HOVER = "rgba(139, 153, 190, 0.45)";
const FIELD_SHADOW_FOCUS = "rgba(29, 104, 71, 0.27)";
const PLACEHOLDER_INK = "#999999";
const REQUIRED_MARK = "#FF7071";
const DANGER = "#DA2424";
const DANGER_TINT = "#FCE7E7";
/** `.pop-up_form-sucess_wrap`: the wash at the 60% it is laid on. */
const SENT_GROUND = "rgba(233, 238, 241, 0.6)";

export const LABEL = {
  mb: "4px",
  color: marketingPalette.ink,
  fontSize: "0.82rem",
  fontWeight: 400,
  lineHeight: "130%",
};

/** `.pop-up_lbl.is-bigger`: a question set as written, larger and heavier. */
const QUESTION_LABEL = {
  fontSize: "0.94rem",
  fontWeight: 500,
};

const FIELD = {
  width: 1,
  px: "1rem",
  fontSize: 14,
  color: FIELD_INK,
  // `.w-input`'s own white, which shows where a form sits on grey.
  backgroundColor: "common.white",
  border: `1px solid ${FIELD_RULE}`,
  borderRadius: "0.5rem",
  boxShadow: `0 1px 2px ${FIELD_SHADOW}`,
  "&:hover": {
    borderColor: marketingPalette.inkSubtle,
    boxShadow: `0 1px 2px ${FIELD_SHADOW_HOVER}`,
  },
  "&.Mui-focused": {
    borderColor: ACCENT,
    boxShadow: `0 1px 2px ${FIELD_SHADOW_FOCUS}`,
  },
  "&.Mui-error": { borderColor: DANGER },
  // Normalize leaves the UA's placeholder opacity in place, which would render
  // the measured grey lighter than the published one.
  "& ::placeholder": { color: PLACEHOLDER_INK, opacity: 1 },
  "& input, & textarea": { p: 0, height: "auto" },
};

export const ERROR_TEXT = {
  mt: "4px",
  color: DANGER,
  fontSize: 13,
  lineHeight: "18px",
};

const FIELD_BLOCK = {
  display: "flex",
  flexDirection: "column",
  mb: "0.75rem",
};

const SENT_PANEL = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "2rem",
  p: "2.7rem 2rem",
  borderRadius: "1rem",
  backgroundColor: SENT_GROUND,
  color: marketingPalette.ink,
  textAlign: "center",
  fontSize: 20,
  fontWeight: 500,
  lineHeight: "140%",
};

/** `.pop-up_form-error_wrap`: under the button, as the published one sits under the form. */
const FAILED_LINE = {
  mt: "10px",
  p: "10px",
  borderRadius: "0.5rem",
  backgroundColor: DANGER_TINT,
  color: DANGER,
  fontSize: 14,
  lineHeight: "21px",
  textAlign: "center",
};

const BOTTOM_ROW = {
  display: "flex",
  alignItems: "center",
  gap: "1.5rem",
  mt: "1rem",
};

const DISCLAIMER_TEXT = {
  color: marketingPalette.ink,
  fontSize: "0.75rem",
  lineHeight: "130%",
};

const SUBMIT = {
  flex: "0 0 auto",
  px: "1.5rem",
  py: "0.75rem",
  borderRadius: "0.5rem",
  backgroundColor: ACCENT,
  color: "common.white",
  // `ButtonBase` leaves the UA's own button font in place.
  fontFamily: "inherit",
  fontSize: 15,
  fontWeight: 500,
  // The published control is an `input`, which takes the body's leading;
  // `ButtonBase` sets its own, and the button comes out 4.5px short without it.
  lineHeight: 1.5,
  "&:hover": { backgroundColor: marketingPalette.accentHover },
  "&.Mui-disabled": { color: "common.white", opacity: 0.6 },
};

export type Field<Name extends string = string> = {
  name: Name;
  /** As the published label renders it: `.pop-up_lbl` capitalises every word. */
  label: string;
  type: string;
  autoComplete: string;
  maxLength: number;
  /** The published `minlength`, or 1 where the field only carries `required`. */
  minLength: number;
  /** The published `data-msg` where there is one, and this form's own where not. */
  invalid: string;
};

/** Where a published form is: being filled in, on its way, thanked for, or refused. */
type Status = "editing" | "sending" | "sent" | "failed";

type Errors<Values> = Partial<Record<keyof Values, string>>;

/**
 * What both published forms check first: each field against its minimum
 * length, in the order the form asks them, then the email's shape. A form's
 * own checks go after, so the first key is the topmost field to fix.
 */
export function fieldErrors<Name extends string>(
  fields: ReadonlyArray<Field<Name>>,
  values: Record<Name | "email", string>
): Errors<Record<Name | "email", string>> {
  const errors: Errors<Record<Name | "email", string>> = {};
  for (const field of fields) {
    if (values[field.name].trim().length < field.minLength) {
      errors[field.name] = field.invalid;
    }
  }
  if (!errors.email && !EMAIL_SHAPE.test(values.email.trim())) {
    errors.email = EMAIL_INVALID;
  }
  return errors;
}

/**
 * A form's state as both published forms keep it: the values, what is wrong
 * with them and how far sending got, each field's id and its error line's
 * under one scope, and an edit that clears the field's error.
 */
export function useInquiry<Values extends Record<string, string>>(
  empty: Values
) {
  const scope = useId();
  const [values, setValues] = useState(empty);
  const [errors, setErrors] = useState<Errors<Values>>({});
  const [status, setStatus] = useState<Status>("editing");

  function edit<Name extends keyof Values>(name: Name, value: Values[Name]) {
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  }

  /** Shows what a check found and names the topmost field still to fix. */
  function firstProblem(found: Errors<Values>): keyof Values | undefined {
    setErrors(found);
    return (Object.keys(found) as Array<keyof Values>)[0];
  }

  return {
    scope,
    values,
    errors,
    status,
    setStatus,
    edit,
    firstProblem,
    fieldId: (name: keyof Values) => `${scope}${String(name)}`,
    errorId: (name: keyof Values) => `${scope}${String(name)}-error`,
  };
}

export function RequiredMark() {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{ color: REQUIRED_MARK, fontSize: "1rem", lineHeight: "70%" }}
    >
      {" *"}
    </Box>
  );
}

/** What replaces the form once it is away, as the published one does. */
export function SentPanel({ text }: { text: string }) {
  return (
    <Box role="status" sx={SENT_PANEL}>
      <Box
        aria-hidden
        sx={{ width: SENT_LOTTIE_SIZE, height: SENT_LOTTIE_SIZE }}
      >
        <MarketingLottie
          path={SENT_LOTTIE}
          loop
          play
          style={{ width: "100%", height: "100%" }}
        />
      </Box>
      <Box>{text}</Box>
    </Box>
  );
}

function FailedLine() {
  return (
    <Typography role="alert" sx={FAILED_LINE}>
      {FAILED_TEXT}
    </Typography>
  );
}

export type ControlProps = {
  value: string;
  error?: string;
  /** The id the label points at, and the one a failed submit focuses. */
  fieldId: string;
  errorId: string;
  onChange: (value: string) => void;
};

export function FormField({
  spec,
  multiline,
  placeholder,
  question,
  value,
  error,
  fieldId,
  errorId,
  onChange,
}: ControlProps & {
  spec: Field;
  multiline?: boolean;
  placeholder?: string;
  /** `.pop-up_lbl.is-bigger`: a question, with no mark though it is required. */
  question?: boolean;
}) {
  return (
    <Box sx={FIELD_BLOCK}>
      <Box
        component="label"
        htmlFor={fieldId}
        sx={question ? [LABEL, QUESTION_LABEL] : LABEL}
      >
        {spec.label}
        {question ? null : <RequiredMark />}
      </Box>
      <InputBase
        id={fieldId}
        type={spec.type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        multiline={multiline}
        minRows={multiline ? 2 : undefined}
        placeholder={placeholder}
        required
        error={Boolean(error)}
        inputProps={{
          maxLength: spec.maxLength,
          autoComplete: spec.autoComplete,
          "aria-describedby": error ? errorId : undefined,
        }}
        sx={[
          FIELD,
          multiline
            ? { p: "1rem", "& ::placeholder": { fontSize: 13 } }
            : { height: "2.63rem" },
        ]}
      />
      {error ? (
        <Typography id={errorId} role="alert" sx={ERROR_TEXT}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * `.pop-up_button-bottom_wrap`: the disclaimer beside Send, a column with Send
 * on top on a phone, and the refusal under both when the form did not go.
 */
export function FormFooter({ status }: { status: Status }) {
  return (
    <>
      <Box
        sx={[
          BOTTOM_ROW,
          {
            [TINY]: {
              flexDirection: "column-reverse",
              alignItems: "stretch",
              gap: "0.5rem",
            },
          },
        ]}
      >
        <Typography sx={[DISCLAIMER_TEXT, { [TINY]: { fontSize: "0.7rem" } }]}>
          {DISCLAIMER}
          {/* The published link takes the paragraph's own ink and no
              underline, which leaves nothing to tell it from the prose
              around it; the ink is kept and the underline is not. */}
          <Box
            component="a"
            href="/privacy-policy"
            target="_blank"
            rel="noreferrer"
            sx={{ color: "inherit" }}
          >
            Privacy Policy.
          </Box>
        </Typography>
        <ButtonBase type="submit" disabled={status === "sending"} sx={SUBMIT}>
          {status === "sending" ? SUBMIT_WAIT_LABEL : SUBMIT_LABEL}
        </ButtonBase>
      </Box>
      {status === "failed" ? <FailedLine /> : null}
    </>
  );
}
